import { zodResolver } from '@hookform/resolvers/zod';
import { useT } from '@transifex/react';
import { Text, VStack } from '@vidahealth/vida-ui';
import { useRouter } from 'expo-router';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Text as RNText } from 'react-native';

import { trackEvent } from '@/modules/analytics';
import { formatBirthdayForData, formatBirthdayForDisplay } from '@/modules/localization';
import { useRulesEngine } from '@/modules/rulesEngine';
import { LoadingScreen, Screen, TextField } from '@/modules/ui';

import { useSignIn } from '../hooks/useSignIn';
import { purlDobSchema, type PurlDobFormValues } from '../schema/accountSchema';

import { AccountHeader } from './AccountHeader';
import { Footer } from './Footer';
import { SignInBottomSheet } from './SignInBottomSheet';
import { WelcomeBottomSheet } from './WelcomeBottomSheet';

// The dedicated PURL entry screen (Sparta's PurlDobVerification): a welcome
// drawer + a single date-of-birth field the member confirms to prove identity.
// Continue calls verify-dob (3-attempt / lockout); on `verified` the rules engine
// advances to account creation (the verified DOB is passed to sign-up). This
// screen is reached only when a valid token is present and no account exists yet.
export function PurlDobVerificationScreen() {
  const t = useT();

  // The org name was resolved at validate time and lives on `purl` — read it from
  // state rather than re-reading the `?org=` URL param.
  const router = useRouter();
  const { purl, verifyDob, verifyAnotherWay } = useRulesEngine();
  const { control, handleSubmit, setError } = useForm<PurlDobFormValues>({
    resolver: zodResolver(purlDobSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    // Seed `birthday: ''` so an empty submit hits the field's regex message
    // ("Enter your date of birth as MM / DD / YYYY") rather than zod's generic
    // "expected string, received undefined" for an unset value.
    defaultValues: { birthday: '' },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Attempts left after a DOB mismatch — drives the "verify another way" escape
  // text below the form (Sparta keeps the count out of the field error).
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const { signInOpen, setSignInOpen, onSignIn } = useSignIn();
  // Fire the welcome-drawer + DOB-form shown events once; count DOB attempts.
  const hasFiredShownRef = useRef(false);
  const dobAttemptCountRef = useRef(0);

  const orgName = purl.orgName;
  const title = orgName
    ? t("You're almost in — Vida is offered to you by {org}", { org: orgName })
    : t("You're almost in");

  // "Verify another way" → abandon PURL (so the resolver won't route back to this
  // screen) and redirect to the app root to restart onboarding. Mirrors Sparta's
  // link to /clients/onboarding.
  const onVerifyAnotherWay = () => {
    verifyAnotherWay();
    router.replace('/');
  };

  const onWelcomeDismiss = () => {
    trackEvent('purl-welcome-drawer-dismissed', {});
    setWelcomeOpen(false);
  };

  const onContinue = handleSubmit(async (values) => {
    setSubmitError(null);
    setAttemptsRemaining(null);
    dobAttemptCountRef.current += 1;
    trackEvent('purl-dob-submit-attempted', { attemptNumber: dobAttemptCountRef.current });
    const [dobYear, dobMonth, dobDay] = formatBirthdayForData(values.birthday).split('-');
    try {
      setIsSubmitting(true);
      const result = await verifyDob({ dobYear, dobMonth, dobDay });
      if (result.status === 'mismatch') {
        // Match Sparta: the field error is the plain "doesn't match" message; the
        // attempts count + "verify another way" escape lives below the form.
        setError('birthday', {
          message: t("That date of birth doesn't match our records. Please try again."),
        });
        setAttemptsRemaining(result.attemptsRemaining);
      } else if (result.status === 'error') {
        setSubmitError(t('We couldn’t verify your date of birth. Please try again.'));
      }
      // 'verified' → advances to account creation; 'locked' → usePurl flips the
      // status so the resolver falls through to normal account creation. Both are
      // driven by the rules engine — nothing to do here.
    } finally {
      setIsSubmitting(false);
    }
  });

  // Once the token validates and the form renders, open the welcome drawer and
  // fire the shown events (once) — mirrors Sparta's PurlDobVerification mount.
  useEffect(() => {
    if (purl.status !== 'valid' || hasFiredShownRef.current) return;
    hasFiredShownRef.current = true;
    setWelcomeOpen(true);
    trackEvent('purl-welcome-drawer-shown', {});
    trackEvent('purl-dob-form-shown', {});
  }, [purl.status]);

  // Hold a loader until the token resolves to `valid` so the DOB form doesn't
  // flash first. An invalid / already-enrolled token never lands here for long —
  // usePurl redirects it to the backend's redirectUrl at validate time.
  if (purl.status !== 'valid') {
    return <LoadingScreen />;
  }

  return (
    <>
      <Screen
        footer={
          <Footer
            showSignIn
            isSubmitting={isSubmitting}
            onContinue={onContinue}
            onSignIn={onSignIn}
            space='2xl'
          />
        }
      >
        <VStack space='2xl'>
          <AccountHeader
            title={title}
            subtitle={t(
              'To get started, confirm your date of birth so we can securely find your account.'
            )}
          />

          <TextField
            control={control}
            name='birthday'
            label={t('Date of birth')}
            placeholder={t('MM / DD / YYYY')}
            inputMode='numeric'
            keyboardType='number-pad'
            maxLength={14}
            format={formatBirthdayForDisplay}
            testID='purl-dob'
          />
          {/* Submit-level failures (lockout / verify error) read as feedback on the
              DOB attempt, so they sit under the field — mismatch uses the field's
              own inline error via setError('birthday'). */}
          {submitError ? (
            <Text size='sm' className='text-error-700'>
              {submitError}
            </Text>
          ) : null}

          {/* DOB mismatch escape (mirrors Sparta): attempts count + a "verify
              another way" link that abandons PURL for normal onboarding. */}
          {attemptsRemaining != null ? (
            <Text size='sm' className='text-typography-600'>
              {t('You have {count} attempts remaining. Prefer to verify another way?', {
                count: attemptsRemaining,
              })}{' '}
              <RNText
                onPress={onVerifyAnotherWay}
                role='link'
                className='text-sm text-primary-700 underline'
                testID='purl-verify-another-way'
              >
                {t('Click here.')}
              </RNText>
            </Text>
          ) : null}
        </VStack>
      </Screen>

      <WelcomeBottomSheet
        isOpen={welcomeOpen}
        onClose={onWelcomeDismiss}
        onContinue={onWelcomeDismiss}
      />

      <SignInBottomSheet isOpen={signInOpen} onClose={() => setSignInOpen(false)} />
    </>
  );
}
